import { Refresh } from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import CookieJs from "js-cookie";
import moment from "moment";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink, useParams } from "react-router-dom";
import Breadcrumb from "../components/Breadcrumb";
import MainLayout from "../components/MainLayout";
import ProtectedPermissionPage from "../components/ProtectedPermissionPage";
import useNotification from "../hooks/useNotification";
import eventService from "../services/event";
import ticketService from "../services/ticket";
import uploadJobsService from "../services/uploadJobs";
import queryDefault from "../utils/queryDefault";

export default function UploadManagement() {
  const notification = useNotification();
  const { event_id } = useParams();
  const user = JSON.parse(CookieJs.get("USER") || "");
  const [refreshInterval, setRefreshInterval] = useState<number | false>(false);

  // Query to get event details
  const { data: eventData, isLoading: eventLoading } = useQuery(
    ["event-detail", event_id],
    () => eventService.viewEventDetail(event_id),
    {
      enabled: !!event_id,
    }
  );

  // Query to get upload jobs
  const {
    data: uploadJobsData,
    isLoading: jobsLoading,
    refetch: refetchJobs,
  } = useQuery(
    ["upload-jobs", event_id],
    () => uploadJobsService.getUploadJobs(Number(event_id), {
      page_number: 1,
      per_page: 50,
    }),
    {
      enabled: !!event_id,
      refetchInterval: refreshInterval,
      onSuccess: (data) => {
        // Check if there are any processing jobs
        const hasProcessingJobs = data?.data?.some(
          (job: any) => job.status === "processing" || job.status === "pending"
        );
        
        if (hasProcessingJobs) {
          setRefreshInterval(3000); // Refresh every 3 seconds
        } else {
          setRefreshInterval(false); // Stop auto-refresh
        }
      },
    }
  );

  // Upload mutations
  const uploadTicket = useMutation("upload_ticket", ticketService.uploadTicket, {
    onSuccess: () => {
      notification.onOpen({
        message: "Ticket upload started successfully!",
        type: "success",
        position: "top",
      });
      refetchJobs();
      setRefreshInterval(3000); // Start auto-refresh
    },
    onError: (error: any) => {
      notification.onOpen({
        message: error?.response?.data?.message || "Failed to upload ticket",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const uploadBulkRedeem = useMutation("uploadBulkRedeem", eventService.uploadBulkRedeem, {
    onSuccess: () => {
      notification.onOpen({
        message: "Bulk redeem upload started successfully!",
        type: "success",
        position: "top",
      });
      refetchJobs();
      setRefreshInterval(3000); // Start auto-refresh
    },
    onError: (error: any) => {
      notification.onOpen({
        message: error?.response?.data?.message || "Failed to upload bulk redeem",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  // File upload handlers
  const handleTicketFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formData = new FormData();
    formData.append("event_id", event_id!);
    event.target.files && formData.append("file", event.target.files[0]);
    formData.append("username", user.username);
    uploadTicket.mutate(formData);
  };

  const handleBulkRedeemFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formData = new FormData();
    formData.append("event_id", event_id!);
    event.target.files && formData.append("file", event.target.files[0]);
    formData.append("username", user.username);
    uploadBulkRedeem.mutate(formData);
  };

  // Status color mapping
  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "success";
      case "processing":
        return "warning";
      case "pending":
        return "info";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  // Progress calculation
  const getProgressPercentage = (processed: number, total: number) => {
    if (!total || total === 0) return 0;
    return Math.round((processed / total) * 100);
  };

  return (
    <MainLayout>
      <Breadcrumb
        label="Upload Management"
        breadcrumbs={[
          { label: "Events", href: "/app/events" },
          { 
            label: eventData?.data?.name || "Event", 
            href: `/app/event/${event_id}` 
          },
        ]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 7, 99]}>
        <Box sx={{ mt: 2 }}>
          {/* Event Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                {eventData?.data?.name || "Loading..."}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage file uploads for tickets and bulk redeem operations
              </Typography>
            </CardContent>
          </Card>

          {/* Upload Actions */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Upload Files
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    <Typography variant="subtitle1">Ticket Upload</Typography>
                    <Button
                      variant="contained"
                      component="label"
                      disabled={uploadTicket.isLoading}
                      fullWidth
                    >
                      {uploadTicket.isLoading ? "Uploading..." : "Upload Ticket File"}
                      <input onChange={handleTicketFileChange} type="file" hidden />
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      component={RouterLink}
                      to="/UploadTemplateTicket.xlsx"
                      target="_blank"
                      fullWidth
                    >
                      Download Template
                    </Button>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    <Typography variant="subtitle1">Bulk Redeem Upload</Typography>
                    <Button
                      variant="contained"
                      component="label"
                      disabled={uploadBulkRedeem.isLoading}
                      fullWidth
                    >
                      {uploadBulkRedeem.isLoading ? "Uploading..." : "Upload Bulk Redeem File"}
                      <input onChange={handleBulkRedeemFileChange} type="file" hidden />
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      component={RouterLink}
                      to="/TemplateBulkRedeem.xlsx"
                      target="_blank"
                      fullWidth
                    >
                      Download Template
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Upload Jobs Table */}
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">Upload Jobs</Typography>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => refetchJobs()}
                  disabled={jobsLoading}
                >
                  Refresh
                </Button>
              </Box>

              {jobsLoading ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography>Loading jobs...</Typography>
                </Box>
              ) : !uploadJobsData?.data || uploadJobsData.data.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography color="text.secondary">
                    No upload jobs found. Upload a file to see jobs here.
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Job ID</TableCell>
                        <TableCell>Filename</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Progress</TableCell>
                        <TableCell>Rows</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Created Time</TableCell>
                        <TableCell>Completed Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uploadJobsData.data.map((job: any) => (
                        <TableRow key={job.job_id}>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {job.job_id.substring(0, 8)}...
                            </Typography>
                          </TableCell>
                          <TableCell>{job.filename}</TableCell>
                          <TableCell>
                            <Chip
                              label={job.status}
                              color={getStatusColor(job.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ width: "100%", minWidth: 120 }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                <Typography variant="body2">
                                  {getProgressPercentage(job.processed_rows || 0, job.total_rows || 0)}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {job.processed_rows || 0}/{job.total_rows || 0}
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={getProgressPercentage(job.processed_rows || 0, job.total_rows || 0)}
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {job.processed_rows || 0} / {job.total_rows || 0}
                            </Typography>
                          </TableCell>
                          <TableCell>{job.created_by}</TableCell>
                          <TableCell>
                            {moment(job.created_time).format("DD/MM/YYYY HH:mm")}
                          </TableCell>
                          <TableCell>
                            {job.completed_time
                              ? moment(job.completed_time).format("DD/MM/YYYY HH:mm")
                              : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Auto-refresh indicator */}
              {refreshInterval && (
                <Box sx={{ mt: 2, p: 1, bgcolor: "info.light", borderRadius: 1 }}>
                  <Typography variant="body2" color="info.contrastText" sx={{ textAlign: "center" }}>
                    Auto-refreshing every 3 seconds while jobs are processing...
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
